/**
 * Client-side forecast/prediction fallback using Open-Meteo.
 * Used when AppSync public API key auth is unavailable (e.g. expired key).
 */
import { calculateQualityScore } from './prediction';
import {
  calculateGoldenBlueHours,
  getWeatherAtTime,
  type WeatherData,
} from './weather';
import {
  USA_LOCATIONS,
  GLOBAL_LOCATIONS,
  type CuratedLocation,
} from './curatedLocations';

export type ClientPredictionType = 'SUNRISE' | 'SUNSET';

export interface ClientTopOpportunity {
  id: string;
  date: string;
  rank: number;
  locationName: string;
  latitude: number;
  longitude: number;
  timezone: string;
  country: string | null;
  region: string | null;
  type: ClientPredictionType;
  eventTime: string;
  qualityScore: number;
  qualityCategory: string;
  cloudCoverTotal: number | null;
  humidity: number | null;
  visibility: number | null;
  windSpeed: number | null;
  temperature: number | null;
  goldenHourStart: string | null;
  goldenHourEnd: string | null;
  explanation: string | null;
  source: 'CLIENT_FALLBACK';
}

export interface ClientLocationForecast {
  locationId: string | null;
  latitude: number;
  longitude: number;
  date: string;
  timezone: string;
  sunriseTime: string | null;
  sunsetTime: string | null;
  sunriseQuality: number;
  sunsetQuality: number;
  sunriseCategory: string;
  sunsetCategory: string;
  sunriseExplanation: string | null;
  sunsetExplanation: string | null;
  sunriseFactorBreakdown: null;
  sunsetFactorBreakdown: null;
  goldenHourMorningStart: string | null;
  goldenHourMorningEnd: string | null;
  goldenHourEveningStart: string | null;
  goldenHourEveningEnd: string | null;
  blueHourMorningStart: string | null;
  blueHourMorningEnd: string | null;
  blueHourEveningStart: string | null;
  blueHourEveningEnd: string | null;
  cloudCover: number | null;
  cloudCoverLow: number | null;
  cloudCoverMid: number | null;
  cloudCoverHigh: number | null;
  humidity: number | null;
  visibility: number | null;
  windSpeed: number | null;
  temperature: number | null;
  pressure: number | null;
  factors: Record<string, unknown> | null;
  cached: boolean;
  fetchedAt: string;
  expiresAt: string;
}

interface OpenMeteoResponse {
  latitude: number;
  longitude: number;
  timezone?: string;
  hourly: WeatherData['hourly'] & {
    temperature_2m?: number[];
  };
  daily: {
    time: string[];
    sunrise: string[];
    sunset: string[];
  };
}

function toUpperCategory(category: string): string {
  return category.toUpperCase();
}

function findDailyIndex(dailyTimes: string[], date: string): number {
  const idx = dailyTimes.findIndex((t) => t.startsWith(date));
  return idx >= 0 ? idx : 0;
}

/**
 * Fetch weather + sun times from Open-Meteo for a single location.
 */
export async function fetchOpenMeteoForecast(
  latitude: number,
  longitude: number,
  date: string,
  timezone = 'auto'
): Promise<OpenMeteoResponse> {
  const params = new URLSearchParams({
    latitude: String(latitude),
    longitude: String(longitude),
    timezone,
    hourly: [
      'cloud_cover',
      'cloud_cover_low',
      'cloud_cover_mid',
      'cloud_cover_high',
      'relative_humidity_2m',
      'visibility',
      'wind_speed_10m',
      'wind_direction_10m',
      'surface_pressure',
      'temperature_2m',
    ].join(','),
    daily: 'sunrise,sunset',
    start_date: date,
    end_date: date,
  });

  const response = await fetch(`https://api.open-meteo.com/v1/forecast?${params.toString()}`);
  if (!response.ok) {
    throw new Error(`Open-Meteo request failed: ${response.status}`);
  }
  return response.json() as Promise<OpenMeteoResponse>;
}

/**
 * Compute a full location forecast client-side (backend-equivalent shape).
 */
export async function computeLocationForecastClient(options: {
  latitude: number;
  longitude: number;
  date: string;
  locationId?: string;
  timezone?: string;
}): Promise<ClientLocationForecast> {
  const { latitude, longitude, date, locationId } = options;
  const weather = await fetchOpenMeteoForecast(
    latitude,
    longitude,
    date,
    options.timezone || 'auto'
  );

  const dayIdx = findDailyIndex(weather.daily.time, date);
  const sunriseTime = weather.daily.sunrise[dayIdx] || null;
  const sunsetTime = weather.daily.sunset[dayIdx] || null;
  const timezone = weather.timezone || options.timezone || 'UTC';

  const weatherData: WeatherData = { hourly: weather.hourly };

  const sunriseWx = sunriseTime
    ? getWeatherAtTime(weatherData, sunriseTime)
    : getWeatherAtTime(weatherData, `${date}T06:00`);
  const sunsetWx = sunsetTime
    ? getWeatherAtTime(weatherData, sunsetTime)
    : getWeatherAtTime(weatherData, `${date}T18:00`);

  // Open-Meteo visibility is meters; prediction expects meters
  const sunrisePred = calculateQualityScore({
    cloudCoverTotal: sunriseWx.cloudCoverTotal,
    cloudCoverLow: sunriseWx.cloudCoverLow,
    cloudCoverMid: sunriseWx.cloudCoverMid,
    cloudCoverHigh: sunriseWx.cloudCoverHigh,
    humidity: sunriseWx.humidity,
    visibility: sunriseWx.visibility,
    pressure: sunriseWx.pressure || 1013,
  });

  const sunsetPred = calculateQualityScore({
    cloudCoverTotal: sunsetWx.cloudCoverTotal,
    cloudCoverLow: sunsetWx.cloudCoverLow,
    cloudCoverMid: sunsetWx.cloudCoverMid,
    cloudCoverHigh: sunsetWx.cloudCoverHigh,
    humidity: sunsetWx.humidity,
    visibility: sunsetWx.visibility,
    pressure: sunsetWx.pressure || 1013,
  });

  const hours =
    sunriseTime && sunsetTime
      ? calculateGoldenBlueHours(sunriseTime, sunsetTime)
      : null;

  const now = new Date();
  const tempAtSunset =
    sunsetTime && weather.hourly.temperature_2m
      ? (() => {
          const weatherWithTemp = {
            ...weatherData,
            hourly: {
              ...weatherData.hourly,
              // reuse pressure slot lookup by matching time via getWeatherAtTime on pressure only
            },
          };
          void weatherWithTemp;
          const idx = weather.hourly.time.findIndex((t) => t === sunsetTime);
          return idx >= 0 ? weather.hourly.temperature_2m?.[idx] ?? null : null;
        })()
      : null;

  return {
    locationId: locationId || null,
    latitude,
    longitude,
    date,
    timezone,
    sunriseTime,
    sunsetTime,
    sunriseQuality: sunrisePred.score,
    sunsetQuality: sunsetPred.score,
    sunriseCategory: toUpperCategory(sunrisePred.category),
    sunsetCategory: toUpperCategory(sunsetPred.category),
    sunriseExplanation: sunrisePred.explanation,
    sunsetExplanation: sunsetPred.explanation,
    sunriseFactorBreakdown: null,
    sunsetFactorBreakdown: null,
    goldenHourMorningStart: hours?.goldenHourMorningStart ?? null,
    goldenHourMorningEnd: hours?.goldenHourMorningEnd ?? null,
    goldenHourEveningStart: hours?.goldenHourEveningStart ?? null,
    goldenHourEveningEnd: hours?.goldenHourEveningEnd ?? null,
    blueHourMorningStart: hours?.blueHourMorningStart ?? null,
    blueHourMorningEnd: hours?.blueHourMorningEnd ?? null,
    blueHourEveningStart: hours?.blueHourEveningStart ?? null,
    blueHourEveningEnd: hours?.blueHourEveningEnd ?? null,
    cloudCover: sunsetWx.cloudCoverTotal,
    cloudCoverLow: sunsetWx.cloudCoverLow,
    cloudCoverMid: sunsetWx.cloudCoverMid,
    cloudCoverHigh: sunsetWx.cloudCoverHigh,
    humidity: sunsetWx.humidity,
    visibility: Math.round((sunsetWx.visibility / 1000) * 10) / 10, // km for UI
    windSpeed: sunsetWx.windSpeed,
    temperature: tempAtSunset,
    pressure: sunsetWx.pressure,
    factors: sunsetPred.factors as unknown as Record<string, unknown>,
    cached: false,
    fetchedAt: now.toISOString(),
    expiresAt: new Date(now.getTime() + 15 * 60 * 1000).toISOString(),
  };
}

async function mapPool<T, R>(
  items: T[],
  concurrency: number,
  mapper: (item: T, index: number) => Promise<R>
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let next = 0;

  async function worker() {
    while (next < items.length) {
      const i = next++;
      results[i] = await mapper(items[i]!, i);
    }
  }

  const workers = Array.from({ length: Math.min(concurrency, items.length) }, () => worker());
  await Promise.all(workers);
  return results;
}

/**
 * Compute Top 10 opportunities client-side from curated locations.
 */
export async function computeTopOpportunitiesClient(options: {
  date: string;
  region?: 'ALL' | 'USA' | 'GLOBAL';
  type?: ClientPredictionType;
  limit?: number;
}): Promise<ClientTopOpportunity[]> {
  const { date, region = 'ALL', type, limit = 10 } = options;

  let locations: CuratedLocation[] = [];
  if (region === 'USA') {
    locations = USA_LOCATIONS;
  } else if (region === 'GLOBAL') {
    locations = GLOBAL_LOCATIONS;
  } else {
    locations = [...USA_LOCATIONS, ...GLOBAL_LOCATIONS];
  }

  // Cap locations to keep client fallback snappy
  const sample = locations.slice(0, region === 'ALL' ? 28 : 20);

  const forecasts = await mapPool(sample, 5, async (loc) => {
    try {
      const forecast = await computeLocationForecastClient({
        latitude: loc.latitude,
        longitude: loc.longitude,
        date,
      });
      return { loc, forecast };
    } catch {
      return null;
    }
  });

  const opportunities: ClientTopOpportunity[] = [];

  for (const row of forecasts) {
    if (!row) continue;
    const { loc, forecast } = row;

    const push = (
      eventType: ClientPredictionType,
      eventTime: string | null,
      score: number,
      category: string,
      explanation: string | null,
      goldenStart: string | null,
      goldenEnd: string | null
    ) => {
      if (!eventTime) return;
      if (type && type !== eventType) return;
      opportunities.push({
        id: `client-${loc.name}-${date}-${eventType}`,
        date,
        rank: 0,
        locationName: loc.name,
        latitude: loc.latitude,
        longitude: loc.longitude,
        timezone: forecast.timezone,
        country: loc.country,
        region: loc.country === 'USA' ? 'USA' : 'GLOBAL',
        type: eventType,
        eventTime,
        qualityScore: score,
        qualityCategory: category,
        cloudCoverTotal: forecast.cloudCover,
        humidity: forecast.humidity,
        visibility: forecast.visibility,
        windSpeed: forecast.windSpeed,
        temperature: forecast.temperature,
        goldenHourStart: goldenStart,
        goldenHourEnd: goldenEnd,
        explanation,
        source: 'CLIENT_FALLBACK',
      });
    };

    push(
      'SUNRISE',
      forecast.sunriseTime,
      forecast.sunriseQuality,
      forecast.sunriseCategory,
      forecast.sunriseExplanation,
      forecast.goldenHourMorningStart,
      forecast.goldenHourMorningEnd
    );
    push(
      'SUNSET',
      forecast.sunsetTime,
      forecast.sunsetQuality,
      forecast.sunsetCategory,
      forecast.sunsetExplanation,
      forecast.goldenHourEveningStart,
      forecast.goldenHourEveningEnd
    );
  }

  opportunities.sort((a, b) => b.qualityScore - a.qualityScore);

  return opportunities.slice(0, limit).map((opp, index) => ({
    ...opp,
    rank: index + 1,
  }));
}
